/**
 * EU Withdrawal Form — Consultoria.io kit
 * Implements 2-step withdrawal flow per Directive (EU) 2023/2673.
 *
 * Submits a `Desistimiento Submitted` event to Klaviyo via the legacy
 * /api/track endpoint (GET with base64-encoded payload). We deliberately
 * avoid the newer /client/events/ endpoint because it requires a custom
 * `revision` header + Content-Type: application/json, which forces a CORS
 * preflight; Klaviyo's OPTIONS response sets `Access-Control-Allow-Methods:
 * OPTIONS` (excluding POST), so the preflight fails and the request is
 * aborted by the browser. /api/track is a CORS-simple GET that works from
 * any origin and is still supported by Klaviyo — the same endpoint their
 * native onsite tracker uses.
 *
 * Klaviyo Flow listens for the metric and triggers:
 *   1. Transactional acknowledgment email to the customer (with timestamp).
 *   2. Internal alert to the merchant inside the Klaviyo workspace.
 */
(function () {
  'use strict';

  var METRIC_NAME = 'Desistimiento Submitted';
  var TRACK_URL = 'https://a.klaviyo.com/api/track';

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    var form = document.querySelector('[data-eu-withdrawal-form]');
    if (!form) return;

    var step1 = form.querySelector('[data-step="1"]');
    var step2 = form.querySelector('[data-step="2"]');
    var resultEl = form.querySelector('[data-eu-withdrawal-result]');
    var errorEl = form.querySelector('[data-eu-withdrawal-error]');
    var summaryEl = form.querySelector('[data-eu-withdrawal-summary]');
    var successEmailEl = form.querySelector('[data-eu-withdrawal-success-email]');
    var timestampEl = form.querySelector('[data-eu-withdrawal-timestamp]');
    var nextBtn = form.querySelector('[data-eu-withdrawal-next]');
    var backBtn = form.querySelector('[data-eu-withdrawal-back]');
    var submitBtn = form.querySelector('button[type="submit"]');

    if (nextBtn) nextBtn.addEventListener('click', goToStep2);
    if (backBtn) backBtn.addEventListener('click', goToStep1);
    form.addEventListener('submit', handleSubmit);

    function fields() {
      return {
        customer_name: form.elements['customer_name'].value.trim(),
        email: form.elements['email'].value.trim(),
        order_number: form.elements['order_number'].value.trim(),
        reason: (form.elements['reason'] && form.elements['reason'].value.trim()) || '',
        company_id: form.elements['company_id'].value.trim()
      };
    }

    function validate() {
      var ok = true;
      ['customer_name', 'email', 'order_number'].forEach(function (name) {
        var el = form.elements[name];
        var valid = el.value.trim().length > 0 && el.checkValidity();
        el.setAttribute('aria-invalid', valid ? 'false' : 'true');
        if (!valid && ok) { ok = false; el.focus(); }
      });
      return ok;
    }

    function goToStep2() {
      if (!validate()) return;
      var data = fields();
      renderSummary(data);
      step1.hidden = true;
      step2.hidden = false;
      step2.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function goToStep1() {
      step2.hidden = true;
      step1.hidden = false;
      step1.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function renderSummary(data) {
      summaryEl.innerHTML = '';
      var rows = [
        ['Nombre', data.customer_name],
        ['Email', data.email],
        ['Número de pedido', data.order_number]
      ];
      if (data.reason) rows.push(['Motivo', data.reason]);
      rows.forEach(function (r) {
        var dt = document.createElement('dt');
        dt.textContent = r[0];
        var dd = document.createElement('dd');
        dd.textContent = r[1];
        summaryEl.appendChild(dt);
        summaryEl.appendChild(dd);
      });
    }

    function handleSubmit(e) {
      e.preventDefault();
      if (!validate()) { goToStep1(); return; }

      var data = fields();
      if (!data.company_id) {
        showError('No Klaviyo company_id configured.');
        return;
      }

      var submittedAt = new Date().toISOString();
      submitBtn.disabled = true;

      sendKlaviyoEvent(data, submittedAt)
        .then(function () { showSuccess(data, submittedAt); })
        .catch(function (err) {
          console.error('[eu-withdrawal] Klaviyo event failed:', err);
          showError(err && err.message);
        })
        .then(function () { submitBtn.disabled = false; });
    }

    function sendKlaviyoEvent(data, submittedAt) {
      var payload = {
        token: data.company_id,
        event: METRIC_NAME,
        customer_properties: {
          $email: data.email,
          $first_name: firstName(data.customer_name),
          $last_name: lastName(data.customer_name)
        },
        properties: {
          order_number: data.order_number,
          customer_name: data.customer_name,
          reason: data.reason,
          submitted_at: submittedAt,
          source: 'eu-withdrawal-kit',
          shop_domain: window.location.hostname
        },
        time: Math.floor(new Date(submittedAt).getTime() / 1000)
      };
      var encoded = base64(JSON.stringify(payload));
      var url = TRACK_URL + '?data=' + encodeURIComponent(encoded);
      return new Promise(function (resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.onreadystatechange = function () {
          if (xhr.readyState !== 4) return;
          if (xhr.status >= 200 && xhr.status < 300 && xhr.responseText.trim() === '1') {
            resolve(xhr.responseText);
          } else {
            reject(new Error('HTTP ' + xhr.status + ': ' + (xhr.responseText || xhr.statusText || 'event rejected by Klaviyo')));
          }
        };
        xhr.onerror = function () {
          reject(new Error('Network error sending event to Klaviyo'));
        };
        xhr.send();
      });
    }

    function base64(str) {
      // UTF-8 safe base64 encoding for the Klaviyo /api/track data param.
      return btoa(unescape(encodeURIComponent(str)));
    }

    function showSuccess(data, submittedAt) {
      step1.hidden = true;
      step2.hidden = true;
      errorEl.hidden = true;
      successEmailEl.textContent = data.email;
      timestampEl.textContent = 'Solicitud registrada el ' + formatTimestamp(submittedAt);
      resultEl.hidden = false;
      resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function showError(message) {
      errorEl.hidden = false;
      errorEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function firstName(full) {
      var parts = (full || '').trim().split(/\s+/);
      return parts[0] || '';
    }
    function lastName(full) {
      var parts = (full || '').trim().split(/\s+/);
      return parts.slice(1).join(' ');
    }
    function formatTimestamp(iso) {
      try {
        var d = new Date(iso);
        return d.toLocaleString('es-ES', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
      } catch (_) { return iso; }
    }
  }
})();
